import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { IoArrowForwardCircle } from "react-icons/io5";
import { mdiRobot } from "@mdi/js";
import Main from "../../components/layout/Main";
import MdiIcon from "../../components/device/app/MdiIcon";
import { CardSkeletons } from "../../components/device/DeviceCard";
import { runsByDay } from "../../automation/format";
import { useAutomationFeature } from "../../automation/AutomationProvider";
import { useConfirm, useRecentRuns, useRunAutomation, useRunHistory } from "../../automation/useAutomations";
import { RecentRunCard, RunRow, automationOfRun } from "./RunViews";
import { AUTOMATION_PATHS } from "./paths";
import "../../assets/styles/app-automation.css";

// Home's Recent Automations, where Quick Actions were (app/screens/FirstScreen.js):
// what ran lately (✓, or ! with why), ▶ on tap-to-run ones; the heading opens
// every run. Shown only while the home has the Automation feature.
export const RecentAutomationsSection = ({ Heading }) => {
  const navigate = useHistory();
  const { on, lookup } = useAutomationFeature();
  const { runs, reload } = useRecentRuns();
  const { confirm, dialog } = useConfirm();
  const run = useRunAutomation(confirm, reload);
  if (!on) return null;

  return (
    <>
      <Heading title="Recent Automations" icon={IoArrowForwardCircle} onClick={() => navigate.push(AUTOMATION_PATHS.HISTORY)} />
      <div className="hx-row">
        {runs === null ? (
          <CardSkeletons count={3} />
        ) : runs.length ? (
          runs.map((item) => (
            <RecentRunCard
              key={item.id}
              run={item}
              lookup={lookup}
              onOpen={() => navigate.push(AUTOMATION_PATHS.DETAIL, { id: item.automationId })}
              onRun={item.type === "tap" ? () => run(automationOfRun(item)) : undefined}
            />
          ))
        ) : (
          <button type="button" className="hx-empty" onClick={() => navigate.push(AUTOMATION_PATHS.HOME)}>
            <MdiIcon path={mdiRobot} size={40} />
            Automations you run show up here.
          </button>
        )}
      </div>
      {dialog}
    </>
  );
};

// Home › Recent Automations: every run in the home the person may see,
// grouped by day (app/screens/Automation/RecentAutomations.js).
function RecentActivity() {
  const navigate = useHistory();
  const { runs, loading, error, loadMore, next } = useRunHistory();

  const days = useMemo(() => runsByDay(runs), [runs]);

  return (
    <Main title="Recent activity">
      <div className="hx-auto-detail">
        {days.map((day) => (
          <section key={day.title} aria-label={day.title}>
            <h2 className="hx-auto-day">{day.title}</h2>
            <ul className="hx-auto-runs">
              {day.runs.map((item) => (
                <RunRow
                  key={item.id}
                  run={item}
                  onOpen={() => navigate.push(AUTOMATION_PATHS.DETAIL, { id: item.automationId })}
                />
              ))}
            </ul>
          </section>
        ))}
        {!runs.length ? (
          loading ? (
            <div className="hx-skel hx-auto-skel is-tall" />
          ) : (
            <div className="hx-empty" role={error ? "alert" : undefined}>
              {error || "Automations you run show up here."}
            </div>
          )
        ) : null}
        {next ? (
          <button type="button" className="hx-auto-more" onClick={loadMore} disabled={loading}>
            {loading ? "Loading…" : "Show more"}
          </button>
        ) : null}
      </div>
    </Main>
  );
}

export default RecentActivity;
